import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    arrayRemove,
    arrayUnion,
    collection,
    deleteField,
    doc,
    writeBatch,
} from 'firebase/firestore';

import { db } from '../../../firebase.js';
import {
    DEFAULT_WASTE_RECEIVER_COLUMNS,
    DEFAULT_WASTE_RECEIVER_DEPLOYMENT_CONFIG,
    DEFAULT_WASTE_RECEIVER_GROUPING_COLUMNS,
    DEFAULT_WASTE_RECEIVER_SORTING_LIST,
    DEFAULT_WASTE_RECEIVER_USER_EVALUATION_PRIORITIZATION,
    getWasteReceiverResultTemplate,
    getWasteReceiverSystemPrompt,
    normalizeCompanyIds,
    normalizeWasteReceiverDeploymentConfig,
    normalizeWasteReceiverUserEvaluationPrioritization,
    setWasteReceiverResultTemplate,
    setWasteReceiverSystemPrompt,
    WASTE_RECEIVERS_COLLECTION,
} from '../../../common/wasteReceivers.js';
import { listPromptPlaceholders } from '../../../common/promptPlaceholders.js';
import { validateResultTemplate } from '../../../common/resultTemplateApi.js';
import {
    getVisibleTransitionColumns,
    normalizeColumnReferencesToNames,
    normalizeDanishComparable,
    normalizeTransitionColumns,
    replaceColumnReference,
    serializeTransitionWasteReceiver,
    validateTransitionColumns,
} from '../../../common/wasteReceiverSortingListTransition.js';
import {
    describeColumnMigration,
    transformPositionalRowsForColumns,
    validateCanonicalPositionalRows,
} from '../../../common/wasteReceiverSortingListMigrations.js';
import { describeFirestoreError } from '../../../common/firestoreErrors.js';
import { normalizeIdList } from '../setupShared.js';

const cloneColumns = (columns) =>
    normalizeTransitionColumns(columns).map((column) => ({ ...column }));

// A new receiver starts from the typical column set, ids included — ids can only ever be
// set while creating, so leaving them blank by default would make it easy to create a
// receiver with no ids at all without the admin ever noticing. Still fully editable/removable.
const createDefaultColumns = () => cloneColumns(DEFAULT_WASTE_RECEIVER_COLUMNS);

const DEFAULT_NAME_ONLY_GROUPING_COLUMNS = normalizeColumnReferencesToNames(
    DEFAULT_WASTE_RECEIVER_GROUPING_COLUMNS,
    DEFAULT_WASTE_RECEIVER_COLUMNS,
);

const createEmptyDraft = () => ({
    name: '',
    companies: [],
    userIds: [],
    userEvaluationPrioritization: [...DEFAULT_WASTE_RECEIVER_USER_EVALUATION_PRIORITIZATION],
    columns: createDefaultColumns(),
    noShowColumns: [],
    groupingColumns: [...DEFAULT_NAME_ONLY_GROUPING_COLUMNS],
    readAloudColumns: [],
    deploymentConfig: { ...DEFAULT_WASTE_RECEIVER_DEPLOYMENT_CONFIG },
});

// State and writes for the Affaldsmodtagere tab: the draft being edited, the dialogs that
// gate a save, and the save itself. The receiver list stays on the page, which subscribes
// to it for all three tabs.
const useWasteReceiverTab = ({
    isActive,
    wasteReceivers,
    companyOptions,
    setCompanyOptions,
    userOptions,
    companyById,
    wasteReceiverById,
    saving,
    setSaving,
    showMessage,
    loadingUsers,
    userLoadError,
    getCompanyDisplayLabel,
    getWasteReceiverDisplayLabel,
}) => {
    const [selectedReceiverId, setSelectedReceiverId] = useState('');
    const [draftMode, setDraftMode] = useState('view');
    const [draft, setDraft] = useState(createEmptyDraft);
    const [draftColumnOrigins, setDraftColumnOrigins] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [systemPromptDialogOpen, setSystemPromptDialogOpen] = useState(false);
    const [resultTemplateDialogOpen, setResultTemplateDialogOpen] = useState(false);
    // Findings from the save gate, handed to the dialog so a blocked save marks its lines.
    const [resultTemplateFindings, setResultTemplateFindings] = useState(null);
    const [pendingCompanyChange, setPendingCompanyChange] = useState(null);
    const [pendingColumnMigration, setPendingColumnMigration] = useState(null);

    const selectedReceiver = useMemo(
        () => wasteReceivers.find((receiver) => receiver.id === selectedReceiverId) || null,
        [wasteReceivers, selectedReceiverId],
    );
    const draftColumnOptions = useMemo(
        () => normalizeTransitionColumns(draft.columns),
        [draft.columns],
    );
    const draftColumnOptionIds = useMemo(
        () => draftColumnOptions.map((column) => column.display_name).filter(Boolean),
        [draftColumnOptions],
    );
    const draftColumnById = useMemo(() => {
        const next = new Map();
        draftColumnOptions.forEach((column) => next.set(column.display_name, column));
        return next;
    }, [draftColumnOptions]);
    const draftGroupingColumnOptionIds = useMemo(
        () => getVisibleTransitionColumns(draft.columns, draft.noShowColumns)
            .map((column) => column.display_name),
        [draft.columns, draft.noShowColumns],
    );
    const draftGroupingColumns = useMemo(
        () => {
            const visibleNames = new Set(
                getVisibleTransitionColumns(draft.columns, draft.noShowColumns)
                    .map((column) => normalizeDanishComparable(column.display_name)),
            );
            return normalizeColumnReferencesToNames(draft.groupingColumns, draft.columns)
                .filter((name) => visibleNames.has(normalizeDanishComparable(name)));
        },
        [draft.groupingColumns, draft.columns, draft.noShowColumns],
    );
    const draftReadAloudColumns = useMemo(
        () => normalizeColumnReferencesToNames(draft.readAloudColumns, draft.columns),
        [draft.readAloudColumns, draft.columns],
    );
    const draftSystemPrompt = getWasteReceiverSystemPrompt(draft.deploymentConfig);
    const draftSystemPromptPlaceholders = useMemo(
        () => listPromptPlaceholders(draftSystemPrompt),
        [draftSystemPrompt],
    );
    const draftResultTemplate = getWasteReceiverResultTemplate(draft.deploymentConfig);

    // The enlarged prompt window belongs to the edit form, so every way out of it — save,
    // cancel, switching entity type — closes the window too.
    useEffect(() => {
        if (draftMode === 'view') {
            setSystemPromptDialogOpen(false);
            setResultTemplateDialogOpen(false);
            setResultTemplateFindings(null);
        }
    }, [draftMode]);
    const receiverUserCountById = useMemo(() => {
        const next = new Map();
        wasteReceivers.forEach((receiver) => next.set(receiver.id, 0));
        userOptions.forEach((user) => {
            user.wasteReceivers.forEach((receiverId) => {
                next.set(receiverId, (next.get(receiverId) || 0) + 1);
            });
        });
        return next;
    }, [userOptions, wasteReceivers]);
    const selectedReceiverUserIds = useMemo(() => {
        if (!selectedReceiver) return [];
        return userOptions
            .filter((user) => user.wasteReceivers.includes(selectedReceiver.id))
            .map((user) => user.id);
    }, [selectedReceiver, userOptions]);
    const isCreating = draftMode === 'create';
    // Column IDs are only ever set at creation time now; an existing receiver's columns keep
    // whatever ID they already have (still written on save), but editing never shows or
    // changes it, so admins cannot rename a live column's ID out from under stored records.
    const showColumnIds = isCreating;
    const formTitle = isCreating ? 'Ny affaldsmodtager' : 'Rediger affaldsmodtager';

    useEffect(() => {
        if (!isActive) return;
        if (draftMode !== 'view') return;
        if (selectedReceiverId && wasteReceivers.some((receiver) => receiver.id === selectedReceiverId)) {
            return;
        }
        setSelectedReceiverId(wasteReceivers[0]?.id || '');
    }, [draftMode, isActive, selectedReceiverId, wasteReceivers]);

    const startCreate = () => {
        const nextDraft = createEmptyDraft();
        setSelectedReceiverId('');
        setDraft(nextDraft);
        setDraftColumnOrigins(nextDraft.columns.map(() => null));
        setDraftMode('create');
    };

    const startEdit = () => {
        if (!selectedReceiver) return;
        if (loadingUsers) {
            showMessage('Brugerlisten hentes stadig.', 'warning');
            return;
        }
        setDraft({
            name: selectedReceiver.name,
            companies: selectedReceiver.companies,
            userIds: selectedReceiverUserIds,
            userEvaluationPrioritization: normalizeWasteReceiverUserEvaluationPrioritization(
                selectedReceiver.userEvaluationPrioritization,
                selectedReceiverUserIds,
            ),
            columns: cloneColumns(selectedReceiver.columns),
            noShowColumns: normalizeColumnReferencesToNames(
                selectedReceiver.noShowColumns,
                selectedReceiver.columns,
            ),
            groupingColumns: normalizeColumnReferencesToNames(
                selectedReceiver.groupingColumns,
                selectedReceiver.columns,
            ),
            readAloudColumns: normalizeColumnReferencesToNames(
                selectedReceiver.readAloudColumns,
                selectedReceiver.columns,
            ),
            deploymentConfig: normalizeWasteReceiverDeploymentConfig(
                selectedReceiver.deploymentConfig,
            ),
        });
        setDraftColumnOrigins(selectedReceiver.columns.map((_, index) => index));
        setPendingColumnMigration(null);
        setDraftMode('edit');
    };

    const cancelDraft = () => {
        setDraft(createEmptyDraft());
        setDraftColumnOrigins([]);
        setDraftMode('view');
        setDeleteDialogOpen(false);
        setPendingCompanyChange(null);
        setPendingColumnMigration(null);
        if (!selectedReceiverId && wasteReceivers.length > 0) {
            setSelectedReceiverId(wasteReceivers[0].id);
        }
    };

    const applyDraftCompanyIds = (companyIds) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            companies: normalizeCompanyIds(companyIds),
        }));
    };

    const updateDraftCompanyIds = (companyIds) => {
        const nextCompanyIds = normalizeCompanyIds(companyIds);
        const currentCompanyIds = new Set(draft.companies);
        const currentReceiverId = isCreating ? '' : selectedReceiver?.id || '';
        const conflicts = nextCompanyIds
            .filter((companyId) => !currentCompanyIds.has(companyId))
            .map((companyId) => {
                const company = companyById.get(companyId);
                const wasteReceiver = company?.wasteReceiver || '';
                if (!company || !wasteReceiver || wasteReceiver === currentReceiverId) return null;

                return {
                    companyId,
                    companyLabel: getCompanyDisplayLabel(companyId),
                    wasteReceiverId: wasteReceiver,
                    wasteReceiverLabel: getWasteReceiverDisplayLabel(wasteReceiver),
                };
            })
            .filter(Boolean);

        if (conflicts.length > 0) {
            setPendingCompanyChange({
                nextCompanyIds,
                conflicts,
            });
            return;
        }

        applyDraftCompanyIds(nextCompanyIds);
    };

    const closeCompanyReassignmentDialog = () => {
        setPendingCompanyChange(null);
    };

    const confirmCompanyReassignment = () => {
        if (!pendingCompanyChange) return;
        applyDraftCompanyIds(pendingCompanyChange.nextCompanyIds);
        setPendingCompanyChange(null);
    };

    const updateDraftUserIds = (userIds) => {
        const normalizedUserIds = normalizeIdList(userIds);
        setDraft((currentDraft) => ({
            ...currentDraft,
            userIds: normalizedUserIds,
            userEvaluationPrioritization: normalizeWasteReceiverUserEvaluationPrioritization(
                currentDraft.userEvaluationPrioritization,
                normalizedUserIds,
            ),
        }));
    };

    const updateDraftUserEvaluationPrioritization = (userIds) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            userEvaluationPrioritization: normalizeWasteReceiverUserEvaluationPrioritization(
                userIds,
                currentDraft.userIds,
            ),
        }));
    };

    const moveDraftPrioritizedUser = (index, direction) => {
        setDraft((currentDraft) => {
            const current = normalizeWasteReceiverUserEvaluationPrioritization(
                currentDraft.userEvaluationPrioritization,
                currentDraft.userIds,
            );
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= current.length) {
                return currentDraft;
            }

            const next = [...current];
            const [moved] = next.splice(index, 1);
            next.splice(targetIndex, 0, moved);

            return {
                ...currentDraft,
                userEvaluationPrioritization: next,
            };
        });
    };

    const updateDraftNoShowColumns = (columnNames) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            noShowColumns: normalizeColumnReferencesToNames(columnNames, currentDraft.columns),
        }));
    };

    const updateDraftGroupingColumns = (columnNames) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            groupingColumns: normalizeColumnReferencesToNames(columnNames, currentDraft.columns),
        }));
    };

    const updateDraftReadAloudColumns = (columnNames) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            readAloudColumns: normalizeColumnReferencesToNames(columnNames, currentDraft.columns),
        }));
    };

    const updateDraftDisclaimer = (disclaimer) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            deploymentConfig: {
                ...normalizeWasteReceiverDeploymentConfig(currentDraft.deploymentConfig),
                disclaimer,
            },
        }));
    };

    const updateDraftSystemPrompt = (systemPrompt) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            deploymentConfig: setWasteReceiverSystemPrompt(
                currentDraft.deploymentConfig,
                systemPrompt,
            ),
        }));
    };

    const updateDraftResultTemplate = useCallback(({ html, css }) => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            deploymentConfig: setWasteReceiverResultTemplate(currentDraft.deploymentConfig, {
                html,
                css,
            }),
        }));
        // The previous report described the previous draft; keeping it would leave stale
        // lines marked in the editor.
        setResultTemplateFindings(null);
    }, []);

    const addDraftColumn = () => {
        setDraft((currentDraft) => ({
            ...currentDraft,
            columns: [...currentDraft.columns, { display_name: '' }],
        }));
        setDraftColumnOrigins((origins) => [...origins, null]);
    };

    const updateDraftColumn = (index, field, value) => {
        setDraft((currentDraft) => {
            const previousColumn = currentDraft.columns[index];
            const columns = currentDraft.columns.map((column, columnIndex) =>
                columnIndex === index ? { ...column, [field]: value } : column,
            );
            if (field !== 'display_name' || !previousColumn) {
                return { ...currentDraft, columns };
            }

            return {
                ...currentDraft,
                columns,
                noShowColumns: replaceColumnReference(
                    currentDraft.noShowColumns,
                    previousColumn,
                    value,
                    columns,
                ),
                groupingColumns: replaceColumnReference(
                    currentDraft.groupingColumns,
                    previousColumn,
                    value,
                    columns,
                ),
                readAloudColumns: replaceColumnReference(
                    currentDraft.readAloudColumns,
                    previousColumn,
                    value,
                    columns,
                ),
            };
        });
    };

    const removeDraftColumn = (index) => {
        setDraft((currentDraft) => {
            const removedColumn = currentDraft.columns[index];
            const columns = currentDraft.columns.filter((_, columnIndex) => columnIndex !== index);
            const removesColumn = (reference) => {
                const key = normalizeDanishComparable(reference);
                return key !== normalizeDanishComparable(removedColumn?.id) &&
                    key !== normalizeDanishComparable(removedColumn?.display_name);
            };
            return {
                ...currentDraft,
                columns,
                noShowColumns: currentDraft.noShowColumns.filter(removesColumn),
                groupingColumns: currentDraft.groupingColumns.filter(removesColumn),
                readAloudColumns: currentDraft.readAloudColumns.filter(removesColumn),
            };
        });
        setDraftColumnOrigins((origins) => origins.filter((_, columnIndex) => columnIndex !== index));
    };

    const saveDraft = async (options = {}) => {
        const columnMigrationConfirmed = options?.columnMigrationConfirmed === true;
        const name = String(draft.name || '').trim();
        if (!name) {
            showMessage('Navn er påkrævet.', 'warning');
            return;
        }
        if (loadingUsers) {
            showMessage('Brugerlisten hentes stadig.', 'warning');
            return;
        }
        if (userLoadError) {
            showMessage('Brugerlisten skal kunne hentes, før brugertilknytninger kan gemmes.', 'warning');
            return;
        }

        const columnValidation = validateTransitionColumns(draft.columns);
        if (columnValidation.blankColumnNumbers.length > 0) {
            showMessage(
                `Kolonnenavn er påkrævet i kolonne ${columnValidation.blankColumnNumbers.join(', ')}.`,
                'warning',
            );
            return;
        }
        if (columnValidation.duplicateNameGroups.length > 0) {
            const names = columnValidation.duplicateNameGroups
                .flatMap((group) => group.map(({ column }) => column.display_name))
                .join(', ');
            showMessage(`Kolonnenavne skal være unikke: ${names}.`, 'warning');
            return;
        }
        if (columnValidation.invalidLegacyKeyColumnNumbers.length > 0) {
            showMessage(
                `Kolonnenavn skal indeholde bogstaver eller tal i kolonne ${columnValidation.invalidLegacyKeyColumnNumbers.join(', ')}.`,
                'warning',
            );
            return;
        }
        if (columnValidation.legacyKeyCollisionGroups.length > 0) {
            const names = columnValidation.legacyKeyCollisionGroups
                .flatMap((group) => group.map(({ column }) => column.display_name))
                .join(', ');
            showMessage(`Kolonnerne giver samme legacy-nøgle: ${names}.`, 'warning');
            return;
        }

        const columns = columnValidation.columns;
        const noShowColumns = normalizeColumnReferencesToNames(draft.noShowColumns, columns);
        const visibleColumnNames = new Set(
            getVisibleTransitionColumns(columns, noShowColumns)
                .map((column) => normalizeDanishComparable(column.display_name)),
        );
        const groupingColumns = normalizeColumnReferencesToNames(draft.groupingColumns, columns)
            .filter((name) => visibleColumnNames.has(normalizeDanishComparable(name)));
        const readAloudColumns = normalizeColumnReferencesToNames(
            draft.readAloudColumns,
            columns,
        );
        const userEvaluationPrioritization = normalizeWasteReceiverUserEvaluationPrioritization(
            draft.userEvaluationPrioritization,
            draft.userIds,
        );
        const columnMigration = isCreating
            ? null
            : describeColumnMigration(
                selectedReceiver?.columns || [],
                columns,
                draftColumnOrigins,
            );
        let sortingList = isCreating
            ? DEFAULT_WASTE_RECEIVER_SORTING_LIST
            : selectedReceiver?.sortingList || DEFAULT_WASTE_RECEIVER_SORTING_LIST;
        const hasPositionalRows = Array.isArray(sortingList.list) && sortingList.list.length > 0;

        if (hasPositionalRows && columnMigration?.hasChanges) {
            if (!columnMigrationConfirmed) {
                setPendingColumnMigration(columnMigration);
                return;
            }

            const transformedRows = transformPositionalRowsForColumns(
                sortingList.list,
                columnMigration,
            );
            const positionalValidation = validateCanonicalPositionalRows(
                transformedRows,
                columns.length,
            );
            if (positionalValidation.blankFirstValueRows.length > 0) {
                showMessage(
                    `Kolonneændringen giver en tom første kolonne i rækkerne ${positionalValidation.blankFirstValueRows.join(', ')}.`,
                    'warning',
                );
                setPendingColumnMigration(null);
                return;
            }
            if (positionalValidation.duplicateFirstValueRowGroups.length > 0) {
                const duplicateRows = positionalValidation.duplicateFirstValueRowGroups.flat()
                    .sort((left, right) => left - right);
                showMessage(
                    `Kolonneændringen giver dubletter i første kolonne i rækkerne ${duplicateRows.join(', ')}.`,
                    'warning',
                );
                setPendingColumnMigration(null);
                return;
            }

            sortingList = {
                ...sortingList,
                list: positionalValidation.rows,
            };
        }

        setPendingColumnMigration(null);

        // The station renders this template. A draft that does not compile, or that never
        // reads `rows`, deploys a screen with no result on it — so it is blocked here
        // rather than reported after the write. Warnings do not block: a receiver with no
        // speaker has a legitimate reason to drop the read-aloud block.
        const resultTemplate = getWasteReceiverResultTemplate(draft.deploymentConfig);
        if (resultTemplate.html.trim() || resultTemplate.css.trim()) {
            try {
                const report = await validateResultTemplate({
                    html: resultTemplate.html,
                    css: resultTemplate.css,
                });
                setResultTemplateFindings(report);
                if (!report.valid) {
                    const [firstError] = report.errors;
                    showMessage(
                        `Resultatskærmen kan ikke gemmes. ${firstError.line ? `Linje ${firstError.line}: ` : ''}${firstError.message}`,
                        'warning',
                    );
                    setResultTemplateDialogOpen(true);
                    return;
                }
            } catch (error) {
                showMessage(`Resultatskærmen kunne ikke valideres: ${error.message}`, 'warning');
                return;
            }
        }

        setSaving(true);

        try {
            const receiverRef = isCreating
                ? doc(collection(db, WASTE_RECEIVERS_COLLECTION))
                : doc(db, WASTE_RECEIVERS_COLLECTION, selectedReceiver.id);
            const receiverId = receiverRef.id;
            const nextUserIds = new Set(normalizeIdList(draft.userIds));
            const previousCompanyIds = new Set(
                isCreating ? [] : normalizeCompanyIds(selectedReceiver?.companies || []),
            );
            const nextCompanyIds = new Set(normalizeCompanyIds(draft.companies));
            const batch = writeBatch(db);

            const receiverPayload = serializeTransitionWasteReceiver({
                id: receiverId,
                name,
                companies: draft.companies,
                columns,
                noShowColumns,
                groupingColumns,
                readAloudColumns,
                userEvaluationPrioritization,
                sortingList,
                deploymentConfig: draft.deploymentConfig,
            });
            // `merge: true` deep-merges nested maps, so a key the deployment side had since
            // deleted from deployment_config would be resurrected by every save from here.
            // `mergeFields` replaces each written field wholesale instead, which is safe
            // because every field in the payload — deployment_config's unknown keys
            // included — is reconstructed from the receiver as it was loaded.
            batch.set(receiverRef, receiverPayload, { mergeFields: Object.keys(receiverPayload) });

            userOptions.forEach((user) => {
                const currentlyConnected = user.wasteReceivers.includes(receiverId);
                const shouldBeConnected = nextUserIds.has(user.id);

                if (currentlyConnected === shouldBeConnected) return;

                batch.update(doc(db, 'users', user.id), {
                    waste_receivers: shouldBeConnected
                        ? arrayUnion(receiverId)
                        : arrayRemove(receiverId),
                });
            });

            const movedCompanyIdsByPreviousReceiverId = new Map();

            nextCompanyIds.forEach((companyId) => {
                const previousWasteReceiverId = companyById.get(companyId)?.wasteReceiver || '';
                if (
                    previousWasteReceiverId &&
                    previousWasteReceiverId !== receiverId &&
                    wasteReceiverById.has(previousWasteReceiverId)
                ) {
                    if (!movedCompanyIdsByPreviousReceiverId.has(previousWasteReceiverId)) {
                        movedCompanyIdsByPreviousReceiverId.set(previousWasteReceiverId, new Set());
                    }
                    movedCompanyIdsByPreviousReceiverId.get(previousWasteReceiverId).add(companyId);
                }

                batch.set(
                    doc(db, 'companies', companyId),
                    { waste_receiver: receiverId },
                    { merge: true },
                );
            });

            movedCompanyIdsByPreviousReceiverId.forEach((movedCompanyIds, previousWasteReceiverId) => {
                const previousWasteReceiver = wasteReceiverById.get(previousWasteReceiverId);
                if (!previousWasteReceiver) return;

                const previousReceiverPayload = serializeTransitionWasteReceiver({
                    id: previousWasteReceiver.id,
                    name: previousWasteReceiver.name,
                    companies: previousWasteReceiver.companies.filter(
                        (companyId) => !movedCompanyIds.has(companyId),
                    ),
                    columns: previousWasteReceiver.columns,
                    noShowColumns: previousWasteReceiver.noShowColumns,
                    groupingColumns: previousWasteReceiver.groupingColumns,
                    readAloudColumns: previousWasteReceiver.readAloudColumns,
                    sortingList: previousWasteReceiver.sortingList,
                    deploymentConfig: previousWasteReceiver.deploymentConfig,
                });
                batch.set(
                    doc(db, WASTE_RECEIVERS_COLLECTION, previousWasteReceiverId),
                    previousReceiverPayload,
                    { mergeFields: Object.keys(previousReceiverPayload) },
                );
            });

            previousCompanyIds.forEach((companyId) => {
                if (nextCompanyIds.has(companyId)) return;
                const company = companyById.get(companyId);
                if (company?.wasteReceiver && company.wasteReceiver !== receiverId) return;

                batch.update(doc(db, 'companies', companyId), {
                    waste_receiver: deleteField(),
                });
            });

            await batch.commit();

            setCompanyOptions((currentCompanies) =>
                currentCompanies.map((company) => {
                    if (nextCompanyIds.has(company.id)) {
                        return { ...company, wasteReceiver: receiverId };
                    }
                    if (previousCompanyIds.has(company.id) && company.wasteReceiver === receiverId) {
                        return { ...company, wasteReceiver: '' };
                    }
                    return company;
                }),
            );
            setSelectedReceiverId(receiverId);
            setDraft(createEmptyDraft());
            setDraftColumnOrigins([]);
            setDraftMode('view');
            showMessage(isCreating ? 'Affaldsmodtager oprettet.' : 'Affaldsmodtager gemt.');
        } catch (error) {
            console.error('Error saving waste receiver:', error);
            showMessage(
                `Kunne ikke gemme affaldsmodtager: ${describeFirestoreError(error)}`,
                'error',
            );
        } finally {
            setSaving(false);
        }
    };

    const closeColumnMigrationDialog = () => {
        if (!saving) setPendingColumnMigration(null);
    };

    const confirmColumnMigration = () => {
        saveDraft({ columnMigrationConfirmed: true });
    };

    const requestDeleteWasteReceiver = () => {
        if (isCreating || !selectedReceiver) return;
        if (loadingUsers) {
            showMessage('Brugerlisten hentes stadig.', 'warning');
            return;
        }
        if (userLoadError) {
            showMessage('Brugerlisten skal kunne hentes, før affaldsmodtageren kan slettes.', 'warning');
            return;
        }

        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (!saving) setDeleteDialogOpen(false);
    };

    const deleteWasteReceiver = async () => {
        if (isCreating || !selectedReceiver) return;

        setSaving(true);

        try {
            const receiverId = selectedReceiver.id;
            const batch = writeBatch(db);

            batch.delete(doc(db, WASTE_RECEIVERS_COLLECTION, receiverId));

            userOptions.forEach((user) => {
                if (!user.wasteReceivers.includes(receiverId)) return;

                batch.update(doc(db, 'users', user.id), {
                    waste_receivers: arrayRemove(receiverId),
                });
            });

            selectedReceiver.companies.forEach((companyId) => {
                const company = companyById.get(companyId);
                if (company?.wasteReceiver && company.wasteReceiver !== receiverId) return;

                batch.update(doc(db, 'companies', companyId), {
                    waste_receiver: deleteField(),
                });
            });

            await batch.commit();

            setCompanyOptions((currentCompanies) =>
                currentCompanies.map((company) =>
                    selectedReceiver.companies.includes(company.id) &&
                    company.wasteReceiver === receiverId
                        ? { ...company, wasteReceiver: '' }
                        : company,
                ),
            );
            setDeleteDialogOpen(false);
            setSelectedReceiverId(wasteReceivers.find((receiver) => receiver.id !== receiverId)?.id || '');
            setDraft(createEmptyDraft());
            setDraftMode('view');
            showMessage('Affaldsmodtager slettet.');
        } catch (error) {
            console.error('Error deleting waste receiver:', error);
            showMessage('Kunne ikke slette affaldsmodtager.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const reset = useCallback(() => {
        setDraft(createEmptyDraft());
        setDraftColumnOrigins([]);
        setDraftMode('view');
        setDeleteDialogOpen(false);
        setPendingCompanyChange(null);
        setPendingColumnMigration(null);
    }, []);

    return {
        addDraftColumn,
        cancelDraft,
        closeColumnMigrationDialog,
        closeCompanyReassignmentDialog,
        closeDeleteDialog,
        confirmColumnMigration,
        confirmCompanyReassignment,
        deleteDialogOpen,
        deleteWasteReceiver,
        draft,
        draftColumnById,
        draftColumnOptionIds,
        draftColumnOptions,
        draftGroupingColumnOptionIds,
        draftGroupingColumns,
        draftMode,
        draftReadAloudColumns,
        draftResultTemplate,
        draftSystemPrompt,
        draftSystemPromptPlaceholders,
        formTitle,
        isCreating,
        moveDraftPrioritizedUser,
        pendingColumnMigration,
        pendingCompanyChange,
        receiverUserCountById,
        removeDraftColumn,
        requestDeleteWasteReceiver,
        reset,
        resultTemplateDialogOpen,
        resultTemplateFindings,
        saveDraft,
        selectedReceiver,
        selectedReceiverId,
        selectedReceiverUserIds,
        setDraft,
        setResultTemplateDialogOpen,
        setSelectedReceiverId,
        setSystemPromptDialogOpen,
        showColumnIds,
        startCreate,
        startEdit,
        systemPromptDialogOpen,
        updateDraftColumn,
        updateDraftCompanyIds,
        updateDraftDisclaimer,
        updateDraftGroupingColumns,
        updateDraftNoShowColumns,
        updateDraftReadAloudColumns,
        updateDraftResultTemplate,
        updateDraftSystemPrompt,
        updateDraftUserEvaluationPrioritization,
        updateDraftUserIds,
    };
};

export default useWasteReceiverTab;
export { createEmptyDraft };
